import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { WithTranslation } from 'react-i18next'

import { ChildProps, Query } from 'react-apollo'
import { client } from 'redux/redux'
import { InputProps } from './systemSearchQuery'
import { SYSTEM_SEARCH_QUERY } from './systemSearchQuery'
import { SYSTEM_NUMBERS_QUERY } from './systemNumbersQuery'
import { POSITION_IMAGE_QUERY } from './positionImageQuery'

import { getManufacturer, getProjectId } from 'utils'

import _ from 'lodash'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import defaultTo from 'lodash/defaultTo'
import map from 'lodash/map'
import union from 'lodash/union'
import debounce from 'lodash/debounce'

import Button from 'components/ui/button'
import Head from 'components/layout/head'
import PageLayout from 'components/layout/pageLayout'
import Select, { IOption } from 'components/ui/select'
import Slider, { IMark } from 'components/ui/slider'
import Results from 'components/layout/results'
import SelectAccordion, { IHierarchyItem } from 'components/layout/selectAccordion'
import { IResult } from 'components/layout/results/resultElement'

interface NewCalculationProps
  extends ChildProps<InputProps, Response>,
    RouteComponentProps<any>,
    WithTranslation {
  authenticated: boolean
  systemSearchData: any
  positionData: any
}

interface IFilter {
  id: string
  name: string
  min: number
  max: number
  condition: string
  value: number | null
  icon: string
  marks: IMark[]
}

interface IStaticFilter {
  id: string
  name: string
  icon: string
  condition: string
  values: string[]
  selectedValue: string
}

type NewCalculationState = {
  showFilters: boolean
  areaSelect: number | null
  area: number | null
  systemNumber: number | null
  hierarchySelected: number | null
  hierarchy: IHierarchyItem[]
  isLoading: boolean
  filters: IFilter[]
  staticFilters: IStaticFilter[]
  results: IResult[]
}

class NewCalculation extends Component<NewCalculationProps, NewCalculationState> {
  constructor(props: NewCalculationProps) {
    super(props)
    this.state = this.initialState
  }

  initialState = {
    areaSelect: null,
    area: null,
    hierarchySelected: null,
    hierarchy: [],
    systemNumber: null,
    showFilters: true,
    isLoading: false,
    filters: [],
    staticFilters: [],
    results: [],
  }

  addEmptySubHierarchy = (hierarchy: any) => {
    return hierarchy.map((hierarchyItem: any) => {
      hierarchyItem.subHierarchy = []
      return hierarchyItem
    })
  }

  resetFilters = () => {
    const { filters, staticFilters } = this.state

    // Set value of each filter to null
    const resetFilters = filters.map((filter: IFilter) => {
      return {
        ...filter,
        value: null,
      }
    })

    // Set value of each static filter to "no" option
    const resetStaticFilters = staticFilters.map((filter: IStaticFilter) => {
      return {
        ...filter,
        selectedValue: '',
      }
    })

    this.setState(
      {
        filters: resetFilters,
        staticFilters: resetStaticFilters,
      },
      () => this.fetchResults(),
    )
  }

  resetSelects = () => {
    this.setState({
      ...this.initialState,
    })
  }

  handleSliderChange = debounce((key: string, value: number | null) => {
    let newFilters = this.state.filters

    // Search for filter with id
    let item = _.find(newFilters, { id: key })

    /*
     * When found set the new value of the filter
     * and set the new state.
     */
    if (item) {
      item.value = value
      this.setState(
        {
          filters: newFilters,
        },
        () => this.fetchResults(),
      )
    }
  }, 300)

  handleStaticFilterChange = (key: string, value: string) => {
    let newFilters = this.state.staticFilters

    // Search for filter with id
    let item = _.find(newFilters, { id: key })

    /*
     * When found set the new value of the filter
     * and set the new state.
     */
    if (item) {
      item.selectedValue = value
      this.setState(
        {
          staticFilters: newFilters,
        },
        () => this.fetchResults(),
      )
    }
  }

  fetchResults = () => {
    const { staticFilters, filters, area } = this.state

    this.setState({ isLoading: true })

    const numericFilter = filters
      .filter((filter: IFilter) => !isNil(filter.value))
      .map((filter: IFilter) => {
        return {
          id: filter.id,
          condition: filter.condition,
          value: filter.value,
        }
      })

    const textFilter = staticFilters
      .filter(
        (filter: IStaticFilter) => filter.selectedValue !== '', // Filter out filter with "All" option selected
      )
      .map((filter: IStaticFilter) => {
        return {
          id: filter.id,
          condition: filter.condition,
          value: isNil(filter.selectedValue) ? '-' : filter.selectedValue, // Use "No" option for unset filters
        }
      })

    this.props.systemSearchData
      .refetch({
        parentId: area,
        numericFilter,
        textFilter,
      })
      .then((res: any) => {
        const products = get(res, 'data.getSystemSearch.products', [])
        const results = this.transformProducts(products)

        this.setState({
          isLoading: false,
          results,
        })

        this.fetchResultImages(results)
      })
      .catch((e: any) => {
        this.setState({ isLoading: false })
        console.error(e)
      })
  }

  handleHierarchyChange = (hierarchyId: number) => {
    const { area } = this.state

    /*
     * User clicked on the same hierarchy.
     * Do not refetch.
     */
    if (hierarchyId === area) {
      return
    }

    this.setState({
      hierarchySelected: hierarchyId,
      filters: [],
      staticFilters: [],
      isLoading: true,
    })

    this.props.systemSearchData
      .refetch({
        parentId: hierarchyId,
      })
      .then((res: any) => {
        const filters = get(res, 'data.getSystemSearch.filters.range', [])
        const staticFilters = get(res, 'data.getSystemSearch.filters.static', [])
        let subHierarchy = get(res, 'data.getSystemSearch.hierarchy', [])

        // Add empty subHierarchy
        subHierarchy = this.addEmptySubHierarchy(subHierarchy)

        let newHierarchy = this.state.hierarchy

        // Deeply search for IHierarchyItem with hierarchyId
        let item = _(newHierarchy)
          .thru((coll) => {
            return union(coll, map<IHierarchyItem>(coll, 'subHierarchy'))
          })
          .flatten()
          .find({ hierarchyId })

        // Transform and set the new results
        const products = get(res, 'data.getSystemSearch.products', [])
        const results = this.transformProducts(products)

        /*
         * When found append the fetched subHierarchy to to item
         * and set the new hierarchy state.
         * And update area with the new hierarchyId
         */
        if (item) {
          item.subHierarchy = subHierarchy

          this.setState({
            results,
            hierarchy: newHierarchy,
            filters: this.transformFilters(filters),
            staticFilters,
            isLoading: false,
          })
        }

        this.fetchResultImages(results)
      })
      .catch((e: any) => {
        this.setState({ isLoading: false })
        console.error(e)
      })
  }

  handleSearch = (option: IOption) => {
    const { value } = option
    const systemNumber = value !== '' ? Number(value) : null

    if (!isNil(systemNumber)) {
      // Clear previous results and area selection
      this.setState({
        results: [],
        areaSelect: null,
        isLoading: true,
        filters: [],
        staticFilters: [],
        hierarchy: [],
      })

      this.props.positionData
        .refetch({
          positionId: systemNumber,
          companyId: getManufacturer(),
        })
        .then((res: any) => {
          let product = get(res, 'data.getPositionSystemNumbers', undefined)
          if (!isNil(product)) {
            product = this.transformProducts([product])
            this.setState({
              results: [...product],
              isLoading: false,
            })

            this.fetchResultImages([...product])
          } else {
            this.setState({ isLoading: false })
          }
        })
        .catch((e: any) => {
          this.setState({ isLoading: false })
          console.error(e)
        })
    }

    this.setState({
      systemNumber,
      showFilters: !isNil(systemNumber) ? false : true,
    })
  }

  handleAddPosition = (positionId: string) => {
    const { mutate, history } = this.props
    const projectId = getProjectId(this.props)
    const isFastCalc = isNil(projectId) ? true : false

    if (!isNil(mutate)) {
      mutate({
        variables: {
          positionId: String(positionId),
          projectId: defaultTo(projectId, ''),
          isFastCalc,
          companyId: getManufacturer(),
        },
      })
        .then((res) => {
          // Add position was successful
          if (get(res, 'data.addPositionToProject')) {
            const version = get(res, 'data.addPositionToProject.version')
            const positionId = get(res, 'data.addPositionToProject.id')
            const projectId = get(res, 'data.addPositionToProject.project')

            if (isFastCalc) {
              history.push(`/calculations/${version}`)
            } else {
              history.push(`/projects/${projectId}/calculations/${positionId}`)
            }
          }
        })
        .catch((err) => console.error(err))
    }
  }

  transformProducts = (products: IResult[]) =>
    products.map((product: any) => {
      const filters = product.filters.filter((f: any) => f.value != null)
      const attributes = filters.map((filter: any) => {
        if (filter.value !== '-') {
          return {
            ...filter,
            name: `${filter.name}: ${this.getBooleanFilterLabel(filter.value)}`,
            shortName: filter.shortName,
          }
        }

        return { ...filter, name: filter.name, shortName: filter.shortName }
      })

      return {
        id: product.id,
        title: product.title,
        description: product.name,
        attributes: attributes,
        image: product.image,
      }
    })

  transformFilters = (filters: any[]) => {
    if (isNil(filters)) return []

    return filters.map((filter: any) => ({
      ...filter,
      value: null,
      min: -1, // Necessary for "All" selection
      marks: !isNil(filter.predefined_values)
        ? `-1;${filter.predefined_values}` // Add -1 for "All" selection
            .split(';')
            .map((item: string) => ({ value: Number(item.trim()) }))
        : undefined,
    }))
  }

  transformSystemNumbers = (systemNumbers: any[]) => {
    if (isNil(systemNumbers)) return []

    return systemNumbers.map((systemNumber: any) => ({
      label: String(systemNumber.title),
      value: String(systemNumber.id),
    }))
  }

  setImage = (id: number, image: string) => {
    const { results } = this.state

    const newResults = results.map((result) => {
      if (Number(result.id) === id) {
        return {
          ...result,
          image,
        }
      }

      return result
    })

    this.setState({
      results: newResults,
    })
  }

  fetchResultImages = (results: any[]) => {
    results.forEach((result) => {
      const { id } = result

      // Fetch the image and set it in the state
      client
        .query({
          query: POSITION_IMAGE_QUERY,
          variables: { positionId: id, companyId: getManufacturer() },
          fetchPolicy: 'network-only',
        })
        .then((res) => this.setImage(id, get(res, 'data.getPositionImage', '')))
    })
  }

  handleAreaChange = (option: IOption) => {
    const { value: hierarchyId } = option

    if (hierarchyId !== '') {
      // Clear system number
      this.setState({
        filters: [],
        staticFilters: [],
        hierarchy: [],
        systemNumber: null,
        isLoading: true,
      })

      this.props.systemSearchData
        .refetch({
          parentId: hierarchyId,
        })
        .then((res: any) => {
          const filters = get(res, 'data.getSystemSearch.filters.range', [])
          const staticFilters = get(res, 'data.getSystemSearch.filters.static', [])
          const products = get(res, 'data.getSystemSearch.products', [])
          let hierarchy = get(res, 'data.getSystemSearch.hierarchy', [])
          hierarchy = this.addEmptySubHierarchy(hierarchy)

          // Mark this level as root
          hierarchy.map((item: any) => {
            item.isRoot = true
            return item
          })

          const results = this.transformProducts(products)

          this.fetchResultImages(results)

          this.setState({
            showFilters: true,
            systemNumber: null,
            results,
            filters: this.transformFilters(filters),
            staticFilters,
            hierarchy,
            isLoading: false,
          })
        })
        .catch((e: any) => {
          // Error occurred or hierarchy is not available
          this.setState({
            showFilters: true,
            systemNumber: null,
            results: [],
            filters: [],
            staticFilters: [],
            hierarchy: [],
            isLoading: false,
          })

          console.error(e)
        })
    }

    // areaSelect is needed bc otherwise the area select field would be updated when the hierarchy changes
    this.setState({
      hierarchySelected: hierarchyId === '' ? null : Number(hierarchyId),
      areaSelect: hierarchyId === '' ? null : Number(hierarchyId),
      area: Number(option.value),
    })
  }

  getBooleanFilterLabel = (value: string) =>
    value === '' ? 'Alle' : value === 'X' ? 'Ja' : value === '-' ? 'Nein' : value

  render() {
    const { t } = this.props
    const {
      hierarchySelected,
      hierarchy,
      results,
      showFilters,
      areaSelect,
      area,
      systemNumber,
      filters,
      staticFilters,
      isLoading,
    } = this.state
    const projectId = getProjectId(this.props)

    return (
      <PageLayout pageTitle={t('newCalculation')}>
        <Head
          showManufacturerSelect={process.env.REACT_APP_ENV === 'abk'}
          headline="Auswahl Bauteil"
          subheadline="Zurück zur Übersicht"
          linkTo={isNil(projectId) ? '/calculations' : `/projects/${projectId}`}
        />
        <div className="mt-45 px-48">
          <div className="flex flex-row mb-65">
            <div className="w-6/12">
              <Query
                query={SYSTEM_SEARCH_QUERY}
                variables={{ parentId: -1, companyId: getManufacturer() }}
              >
                {({ loading, data }: any | null) => {
                  const hierarchy = get(data, 'getSystemSearch.hierarchy', [])

                  let areaOptions = [
                    {
                      label: 'Bereich auswählen',
                      value: '',
                    },
                  ]

                  areaOptions.push(
                    ...hierarchy.map((elem: any) => {
                      return {
                        label: elem.name,
                        value: elem.hierarchyId,
                      }
                    }),
                  )

                  return (
                    <Select
                      label="Bereich"
                      onChange={(option) => this.handleAreaChange(option)}
                      options={areaOptions}
                      defaultValue=""
                      isSearchable
                      isLoading={loading}
                      value={defaultTo(
                        areaOptions.find((option: IOption) => option.value === areaSelect),
                        areaOptions[0],
                      )}
                    />
                  )
                }}
              </Query>
            </div>
            <div className="w-6/12 ml-30">
              <Query
                query={SYSTEM_NUMBERS_QUERY}
                variables={{
                  parentId: defaultTo(hierarchySelected, -1),
                  companyId: getManufacturer(),
                }}
              >
                {({ loading, data }: any | null) => {
                  const systemNumbers = get(data, 'getSystemnumbers', undefined)

                  let systemNumbersOptions = [
                    {
                      label: 'Systemnummer',
                      value: '',
                    },
                  ]
                  systemNumbersOptions.push(...this.transformSystemNumbers(systemNumbers))

                  return (
                    <Select
                      label="Suche (optional)"
                      onChange={(option) => this.handleSearch(option)}
                      options={systemNumbersOptions}
                      placeholder="Systemnummer"
                      isSearchable
                      isLoading={loading}
                      defaultValue=""
                      value={defaultTo(
                        systemNumbersOptions.find(
                          (option: IOption) => option.value === String(systemNumber),
                        ),
                        systemNumbersOptions[0],
                      )}
                    />
                  )
                }}
              </Query>
            </div>
          </div>

          {(area || systemNumber) && (
            <>
              {showFilters && (
                <div className="flex flex-row mb-30">
                  <div className="flex flex-col flex-1">
                    <div className="flex flex-wrap w-full -mx-15">
                      {filters.map((filter: IFilter, index: number) => (
                        <div
                          key={index}
                          style={{ minWidth: 130 }}
                          className="flex flex-row items-start w-3/12 xl:w-2/12 px-15 mb-30"
                        >
                          {!isEmpty(filter.icon) && (
                            <span
                              className="inline-block flex-shrink-0 w-25 mr-20"
                              dangerouslySetInnerHTML={{ __html: filter.icon }}
                            />
                          )}
                          <div className="break-all">
                            <Slider
                              label={filter.name}
                              min={filter.min}
                              max={filter.max}
                              marks={filter.marks}
                              value={defaultTo(filter.value, -1)}
                              onChange={(value) => this.handleSliderChange(filter.id, value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-end w-full -mx-15">
                      {staticFilters.map((filter: IStaticFilter, index: number) => {
                        const options: IOption[] = [
                          {
                            label: 'Alle',
                            value: '',
                          },
                        ]

                        ;(filter.values || []).forEach((value: string) => {
                          options.push({
                            label: this.getBooleanFilterLabel(value),
                            value,
                          })
                        })

                        return (
                          <div
                            key={index}
                            style={{ minWidth: 130 }}
                            className="flex flex-row items-start w-3/12 px-15 mb-30"
                          >
                            {!isNil(filter.icon) && (
                              <span
                                className="inline-block flex-shrink-0 w-25 mr-20"
                                dangerouslySetInnerHTML={{ __html: filter.icon }}
                              />
                            )}
                            <div className="break-all">
                              <Select
                                label={filter.name}
                                options={options}
                                value={defaultTo(
                                  options.find(
                                    (option: IOption) => option.value === filter.selectedValue,
                                  ),
                                  options[0],
                                )}
                                onChange={(option: IOption) =>
                                  this.handleStaticFilterChange(filter.id, String(option.value))
                                }
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="whitespace-no-wrap ml-30">
                    {(filters.length > 0 || staticFilters.length > 0) && (
                      <Button secondary onClick={this.resetFilters}>
                        Filter zurücksetzen
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between w-full mb-65">
                {showFilters && (
                  <div className="w-4/12">
                    <SelectAccordion
                      selected={hierarchySelected}
                      hierarchy={hierarchy}
                      onChange={(id) => this.handleHierarchyChange(Number(id))}
                    />
                  </div>
                )}
                <div className={`${showFilters ? 'w-8/12 ml-30' : 'w-full'}`}>
                  <Results
                    showCount={showFilters}
                    data={results}
                    isLoading={isLoading}
                    onClick={this.handleAddPosition}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </PageLayout>
    )
  }
}

export default NewCalculation
